import { classifySentence, type ClassificationResult } from './classification';
import { callFeatureLlm } from '../../lib/llmClient';
import { isAiCapabilityEnabled } from '../../lib/ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../../supabase/functions/_shared/modelRequest.ts';

const CLASSIFICATION_SYSTEM_PROMPT =
  'You classify sentences into Outcome Driver components: Current State (facts), Problem/Opportunity (meaning), or Ramifications (consequences). Respond with JSON only.';

/**
 * Suggest the likely bucket for a sentence using LLM when enabled.
 * Falls back to rule-based classification when assist is off or model unavailable.
 */
export async function suggestLikelyBucket(sentence: string): Promise<ClassificationResult> {
  const ruleBasedResult = classifySentence(sentence);

  if (!isAiCapabilityEnabled('outcome_driver_generator')) {
    return ruleBasedResult;
  }

  try {
    const content = await callFeatureLlm({
      userPrompt: buildClassificationPrompt(sentence),
      systemPrompt: CLASSIFICATION_SYSTEM_PROMPT,
      conversationId: 'outcome-driver-classifier-' + Date.now(),
      model: DEFAULT_MODEL,
      temperature: 0.3,
      maxTokens: 200,
    });

    if (!content) return ruleBasedResult;

    const llmResult = parseLLMResponse(content);
    return {
      bucket: llmResult.bucket || ruleBasedResult.bucket,
      confidence: llmResult.confidence || ruleBasedResult.confidence,
      reasoning: llmResult.reasoning || ruleBasedResult.reasoning,
      isCorrect: null,
    };
  } catch (error) {
    console.warn('LLM classification failed, using rule-based:', error);
    return ruleBasedResult;
  }
}

function buildClassificationPrompt(sentence: string): string {
  return `Classify this sentence into one of three Outcome Driver buckets:

1. Current State: Observable facts, things a camera could record. No interpretation or judgment.
2. Problem/Opportunity: The meaning of the facts, the core issue or tension.
3. Ramifications: Consequences if nothing changes, stakes, value judgment.

Sentence: "${sentence}"

Respond with JSON only:
{
  "bucket": "current-state" | "problem-opportunity" | "ramifications",
  "reasoning": "Brief explanation of why this sentence belongs in this bucket"
}`;
}

function parseLLMResponse(content: string): Partial<ClassificationResult> {
  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        bucket: parsed.bucket || null,
        reasoning: parsed.reasoning || '',
        confidence: 'medium' as const,
      };
    }
  } catch (error) {
    console.warn('Failed to parse LLM response:', error);
  }

  const lower = content.toLowerCase();
  if (lower.includes('current state') || lower.includes('current-state')) {
    return { bucket: 'current-state' as const, reasoning: content, confidence: 'medium' as const };
  }
  if (lower.includes('problem') || lower.includes('opportunity')) {
    return { bucket: 'problem-opportunity' as const, reasoning: content, confidence: 'medium' as const };
  }
  if (lower.includes('ramification') || lower.includes('consequence')) {
    return { bucket: 'ramifications' as const, reasoning: content, confidence: 'medium' as const };
  }

  return {};
}
